import { useState, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';

export interface WordAnalysis {
  word: string;
  expectedWord: string;
  status: 'correct' | 'close' | 'incorrect';
  confidenceScore: number;
  startTime?: number;
  endTime?: number;
  phonemeFeedback?: Record<string, any>;
  suggestions?: string;
}

export interface EchoReaderAnalysisResult {
  sentenceIndex: number;
  transcription: string;
  overallScore: number;
  wordAnalysis: WordAnalysis[];
  audioUrl: string;
  feedbackMessage: string;
  language: string;
}

export interface AnalysisState {
  isAnalyzing: boolean;
  results: Map<number, EchoReaderAnalysisResult>;
  error: string | null;
}

export function useEchoReaderAnalysis() {
  const { user } = useAuthStore();
  const [state, setState] = useState<AnalysisState>({
    isAnalyzing: false,
    results: new Map(),
    error: null,
  });

  // Analyze speech for a specific sentence
  const analyzeSpeech = useCallback(async (
    storyId: string,
    sentenceIndex: number,
    sentenceText: string,
    audioBlob: Blob,
    language: string = 'en'
  ): Promise<EchoReaderAnalysisResult | null> => {
    if (!user) {
      setState(prev => ({ ...prev, error: 'User not authenticated' }));
      return null;
    }

    setState(prev => ({ ...prev, isAnalyzing: true, error: null }));

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('user_id', user.id);
      formData.append('story_id', storyId);
      formData.append('sentence_index', sentenceIndex.toString());
      formData.append('sentence_text', sentenceText);
      formData.append('language', language);
      formData.append('audio', audioBlob, `sentence_${sentenceIndex}.wav`);

      const response = await fetch('/api/speech/echo-reader/analyze/upload', {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type - let the browser set it with boundary for FormData
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const result: EchoReaderAnalysisResult = data.data;

      // Store the result
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        results: new Map(prev.results.set(sentenceIndex, result)),
        error: null,
      }));

      return result;
    } catch (error) {
      console.error('Speech analysis failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Speech analysis failed';
      
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        error: errorMessage,
      }));

      return null;
    }
  }, [user]);

  // Get analysis result for a specific sentence
  const getAnalysisResult = useCallback((sentenceIndex: number): EchoReaderAnalysisResult | null => {
    return state.results.get(sentenceIndex) || null;
  }, [state.results]);

  // Get word status for a specific word in a sentence
  const getWordStatus = useCallback((sentenceIndex: number, wordIndex: number): string | null => {
    const result = state.results.get(sentenceIndex);
    if (!result || !result.wordAnalysis || wordIndex >= result.wordAnalysis.length) {
      return null;
    }
    return result.wordAnalysis[wordIndex].status;
  }, [state.results]);

  // Get overall score for a sentence
  const getSentenceScore = useCallback((sentenceIndex: number): number | null => {
    const result = state.results.get(sentenceIndex);
    return result ? result.overallScore : null;
  }, [state.results]);

  // Get feedback message for a sentence
  const getFeedbackMessage = useCallback((sentenceIndex: number): string | null => {
    const result = state.results.get(sentenceIndex);
    return result ? result.feedbackMessage : null;
  }, [state.results]);

  // Get suggestions for a specific word
  const getWordSuggestions = useCallback((sentenceIndex: number, wordIndex: number): string | null => {
    const result = state.results.get(sentenceIndex);
    if (!result || !result.wordAnalysis || wordIndex >= result.wordAnalysis.length) {
      return null;
    }
    return result.wordAnalysis[wordIndex].suggestions || null;
  }, [state.results]);

  // Calculate overall story progress
  const getStoryProgress = useCallback((_totalSentences: number): {
    completed: number;
    averageScore: number;
    totalWords: number;
    correctWords: number;
  } => {
    const completed = state.results.size;
    let totalScore = 0;
    let totalWords = 0;
    let correctWords = 0;

    state.results.forEach((result) => {
      totalScore += result.overallScore;
      totalWords += result.wordAnalysis.length;
      correctWords += result.wordAnalysis.filter(w => w.status === 'correct').length;
    });

    return {
      completed,
      averageScore: completed > 0 ? totalScore / completed : 0,
      totalWords,
      correctWords,
    };
  }, [state.results]);

  // Clear analysis results
  const clearResults = useCallback(() => {
    setState(prev => ({
      ...prev,
      results: new Map(),
      error: null,
    }));
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Retry analysis for a specific sentence
  const retryAnalysis = useCallback(async (
    storyId: string,
    sentenceIndex: number,
    sentenceText: string,
    audioBlob: Blob,
    language: string = 'en'
  ): Promise<EchoReaderAnalysisResult | null> => {
    // Clear previous result for this sentence
    setState(prev => ({
      ...prev,
      results: new Map([...prev.results].filter(([index]) => index !== sentenceIndex)),
    }));

    // Retry analysis
    return analyzeSpeech(storyId, sentenceIndex, sentenceText, audioBlob, language);
  }, [analyzeSpeech]);

  return {
    // State
    ...state,
    
    // Actions
    analyzeSpeech,
    retryAnalysis,
    clearResults,
    clearError,
    
    // Getters
    getAnalysisResult,
    getWordStatus,
    getSentenceScore,
    getFeedbackMessage,
    getWordSuggestions,
    getStoryProgress,
  };
}